'use client'

/**
 * A7: the company's own sign-in app is switched on but cannot sign anyone in right now (branded sign-in, 3 Nov 2026;
 * contract docs/superpowers/specs/2026-11-03-branded-sign-in-design.md section 4.5).
 *
 * HumanFirewall is a security-awareness platform. The API lists such an app under `unavailable` (its secret expired,
 * or the provider refuses it); its owners were already e-mailed. The page says so plainly, and what still works is
 * drawn under it: a passkey, the emergency owner's sign-in, or the methods the company kept on. Amber, not red: nothing
 * the reader did is wrong. The app is named as the company named it ("Sign-in with CSB Microsoft", the label the API's
 * answer carries for an unavailable app too, evidence run X4b), isolated as typed text so an Arabic sentence keeps its
 * order around a Latin name.
 */

import { AlertTriangle } from 'lucide-react'
import { UserText } from '@/components/ui/user-text'
import { useTranslation } from '@/lib/i18n'
import { richText } from '@/lib/i18n/rich-text'

export function SignInUnavailable({ apps, passkeyOffered }: { apps: string[]; passkeyOffered: boolean }) {
  const { t } = useTranslation()
  if (apps.length === 0) return null
  return (
    <div className="mb-6 space-y-2" data-testid="sign-in-unavailable">
      {apps.map((app) => (
        <div
          key={app}
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-400/40 dark:bg-amber-500/10 dark:text-amber-200"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {richText(
              passkeyOffered ? t('signInPage.unavailable.withPasskey') : t('signInPage.unavailable.withoutPasskey'),
              { app: <UserText>{app}</UserText> },
            )}
          </span>
        </div>
      ))}
    </div>
  )
}
